/**
 * Namens-Treffer-Erkennung beim Karten-Import (Befund 31.07.2026, docs/BACKLOG.md).
 *
 * Der Haupt-Einstieg `/katalog/import` (der Knopf "Karte importieren") startete BISHER immer mit
 * `cardMode: "create"` - auch wenn eine Karte gleichen Namens bereits existierte. Ergebnis eines
 * echten Vorfalls: 186 statt 93 Positionen, zwoelf davon doppelt (einmal bepreist, einmal zu
 * 0,00 EUR), weil dieselbe Karte zweimal importiert wurde.
 *
 * Antoines Entscheidung (31.07./01.08.2026): "Ersetzen und vorher fragen." Erkennt der Import
 * einen Namens-Treffer, wird das Ersetzen VORGESCHLAGEN, aber erst nach Bestaetigung ausgefuehrt.
 * `create` bleibt der Default nur, wenn kein Treffer vorliegt.
 *
 * Wahl der Erkennungs-Methode: EIN simpler, clientseitiger Namensvergleich VOR dem KI-Parse-Lauf
 * (`POST /api/menu-imports`) statt danach. Begruendung:
 *  1. `POST /api/menu-imports` ist laut Kommentar dort selbst "die TEUERSTE bezahlte KI-Route des
 *     Systems" (3-8 bezahlte Modellaufrufe je Anfrage, ~30-100s Laufzeit). Ein Treffer VOR diesem
 *     Aufruf spart genau die Kosten, die beim erneuten Hochladen derselben Karte sonst anfallen
 *     waeren.
 *  2. Es gibt keinen von der KI gelieferten "Kartennamen" im geparsten Ergebnis (`ParsedMenu`
 *     enthaelt nur `sections`, keinen Titel) - der einzige Namens-Kandidat VOR dem Commit ist
 *     ohnehin der Datei-Name (dieselbe Ableitung, die menu-import.tsx als `cardName`-Default nach
 *     dem Parse verwendet). Ein Vergleich GEGEN diesen Kandidaten ist also weder ungenauer noch
 *     schneller/langsamer als ein Vergleich NACH dem Parse - er ist nur guenstiger.
 *  3. Kartennamen sind kurz und werden vom Betreiber bewusst getippt/gewaehlt (Auftrag Punkt 1) -
 *     ein KI-Aehnlichkeits-Fuzzy ist hier ausdruecklich NICHT verlangt. Eine enge, deterministische
 *     Normalisierung (Gross-/Kleinschreibung, Rand-/Mehrfach-Leerzeichen) reicht.
 *
 * `GET /api/menu-cards` liefert bereits ausschliesslich NICHT archivierte Karten
 * (`isNull(menuCards.archivedAt)`, apps/api/src/routes/menu-cards.ts) - kein neuer Endpunkt noetig,
 * die Seite laedt diese Liste ohnehin schon als `existingCards`.
 */

export interface Kartennamenskandidat {
  id: string;
  name: string;
}

import { normalisiereKartenname } from "@maestro/katalog-treffer";

/** Normalisierung lebt in `@maestro/katalog-treffer` (dieselbe Regel nutzt dort der Abgleich
 *  Freitext-Position -> Katalog-Position); hier nur weitergereicht. */
export { normalisiereKartenname };

/** Leitet den Namens-Kandidaten aus einem Datei-Namen ab (Endung entfernt, getrimmt) - dieselbe
 *  Ableitung wie der spaetere `cardName`-Default in menu-import.tsx, nur bereits VOR dem Parse
 *  nutzbar. Liefert `null` bei einem (nach Endung-Entfernung) leeren Namen. */
export function kartennameAusDateiname(dateiname: string): string | null {
  const base = dateiname.replace(/\.[a-z0-9]+$/i, "").trim();
  return base || null;
}

/**
 * Sucht unter `existingCards` eine Karte mit (nach Normalisierung) exakt gleichem Namen wie
 * `candidateName`. Liefert `null`, wenn kein Kandidat vorliegt oder keine Karte passt - dann
 * bleibt `create` unveraendert der Default (Regel: kein Treffer -> kein Eingriff).
 */
export function findeKartenNamenstreffer<T extends Kartennamenskandidat>(
  candidateName: string | null,
  existingCards: readonly T[],
): T | null {
  if (!candidateName) return null;
  const norm = normalisiereKartenname(candidateName);
  if (!norm) return null;
  return existingCards.find((k) => normalisiereKartenname(k.name) === norm) ?? null;
}
